export const toDateValue = (date) => {
    if (!date) return '';
    const d = typeof date.toDate === 'function' ? date.toDate() : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
};

export const filterByDateRange = (items, getDate, fromDate, toDate) =>
    items.filter((item) => {
        const raw = getDate(item);
        if (!raw) return false;
        const current = typeof raw.toDate === 'function' ? raw.toDate() : new Date(raw);
        if (Number.isNaN(current.getTime())) return false;
        if (fromDate && current < new Date(fromDate)) return false;
        if (toDate) {
            const end = new Date(toDate);
            end.setHours(23, 59, 59, 999);
            if (current > end) return false;
        }
        return true;
    });

export const exportToCsv = (filename, headers, rows) => {
    const safe = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.map(safe).join(','), ...rows.map((row) => row.map(safe).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const escapePdfString = (text) => String(text).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

const textFromCell = (value) => {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

const normalizeRows = (rows) => rows.map((row) => row.map((cell) => textFromCell(cell)));

const encodeJpegBytes = (dataUrl) => {
    const commaIndex = dataUrl.indexOf(',');
    const base64 = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
    return atob(base64);
};

const renderPdfPageCanvas = ({ title, subtitle, columns, rows, pageNumber, totalPages }) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1240;
    canvas.height = 1754;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const margin = 70;
    const contentWidth = canvas.width - margin * 2;
    let y = margin;

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 42px Kanit, Arial, sans-serif';
    ctx.fillText(title, margin, y);
    y += 56;

    if (subtitle) {
        ctx.fillStyle = '#475569';
        ctx.font = '26px Kanit, Arial, sans-serif';
        ctx.fillText(subtitle, margin, y);
        y += 44;
    }

    ctx.fillStyle = '#64748b';
    ctx.font = '22px Kanit, Arial, sans-serif';
    ctx.fillText(`หน้าที่ ${pageNumber} / ${totalPages}`, margin, y);
    y += 36;

    const tableTop = y;
    const headerHeight = 52;
    const rowHeight = 42;
    const columnCount = Math.max(columns.length, 1);
    const colWidth = contentWidth / columnCount;

    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(margin, tableTop, contentWidth, headerHeight);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(margin, tableTop, contentWidth, headerHeight + rows.length * rowHeight);

    ctx.font = 'bold 22px Kanit, Arial, sans-serif';
    ctx.fillStyle = '#0f172a';

    columns.forEach((col, index) => {
        const x = margin + index * colWidth + 10;
        const clipped = col.length > 28 ? `${col.slice(0, 28)}...` : col;
        ctx.fillText(clipped, x, tableTop + 34);
    });

    ctx.font = '20px Kanit, Arial, sans-serif';
    rows.forEach((row, rowIndex) => {
        const rowY = tableTop + headerHeight + rowIndex * rowHeight;
        if (rowIndex % 2 === 0) {
            ctx.fillStyle = '#f8fafc';
            ctx.fillRect(margin, rowY, contentWidth, rowHeight);
        }

        ctx.fillStyle = '#0f172a';
        row.forEach((cell, colIndex) => {
            const x = margin + colIndex * colWidth + 10;
            const text = textFromCell(cell);
            const clipped = text.length > 32 ? `${text.slice(0, 32)}...` : text;
            ctx.fillText(clipped, x, rowY + 28);
        });
    });

    return canvas;
};

const buildPdfFromJpegs = (jpegPages, filename) => {
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const objects = [];

    const pushObject = (content) => {
        objects.push(content);
        return objects.length;
    };

    const pageObjectIds = [];

    const catalogId = pushObject('<< /Type /Catalog /Pages 2 0 R >>');
    const pagesId = pushObject('<< /Type /Pages /Kids [] /Count 0 >>');

    jpegPages.forEach((page, index) => {
        const imageId = pushObject(
            `<< /Type /XObject /Subtype /Image /Width ${page.width} /Height ${page.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${page.bytes.length} >>\nstream\n${page.bytes}\nendstream`
        );

        const scale = Math.min(pageWidth / page.width, pageHeight / page.height);
        const drawWidth = page.width * scale;
        const drawHeight = page.height * scale;
        const x = (pageWidth - drawWidth) / 2;
        const y = (pageHeight - drawHeight) / 2;
        const contentStream = `q\n${drawWidth.toFixed(2)} 0 0 ${drawHeight.toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)} cm\n/Im${index + 1} Do\nQ`;
        const contentId = pushObject(`<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`);

        const pageId = pushObject(
            `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im${index + 1} ${imageId} 0 R >> >> /Contents ${contentId} 0 R >>`
        );
        pageObjectIds.push(pageId);
    });

    objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageObjectIds.length} >>`;
    objects[catalogId - 1] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;

    let pdf = '%PDF-1.4\n';
    const offsets = [0];
    objects.forEach((content, index) => {
        offsets.push(pdf.length);
        pdf += `${index + 1} 0 obj\n${content}\nendobj\n`;
    });

    const xrefStart = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';
    offsets.slice(1).forEach((offset) => {
        pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
    });
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

    const bytes = new Uint8Array(pdf.length);
    for (let i = 0; i < pdf.length; i += 1) {
        bytes[i] = pdf.charCodeAt(i) & 0xff;
    }

    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const exportTableToPdf = ({ filename, title, subtitle = '', columns = [], rows = [] }) => {
    if (!columns.length) return false;

    const normalizedRows = normalizeRows(rows);
    const rowsPerPage = 30;
    const totalPages = Math.max(Math.ceil(normalizedRows.length / rowsPerPage), 1);
    const pages = [];

    for (let i = 0; i < totalPages; i += 1) {
        const chunk = normalizedRows.slice(i * rowsPerPage, (i + 1) * rowsPerPage);
        const canvas = renderPdfPageCanvas({
            title,
            subtitle,
            columns,
            rows: chunk,
            pageNumber: i + 1,
            totalPages,
        });
        const bytes = encodeJpegBytes(canvas.toDataURL('image/jpeg', 0.92));
        pages.push({ width: canvas.width, height: canvas.height, bytes });
    }

    buildPdfFromJpegs(pages, filename || `${escapePdfString(title || 'report')}.pdf`);
    return true;
};

export const exportTextAsPdfViaPrint = (title, lines) => {
    const rows = (lines || []).map((line) => [line]);
    return exportTableToPdf({
        filename: `${(title || 'report').replace(/\s+/g, '-').toLowerCase()}.pdf`,
        title: title || 'รายงาน',
        columns: ['รายละเอียด'],
        rows,
    });
};
